/**
 * User API — custom name-splitting logic for Okta-backed user management.
 */

import { apiFetch } from './fetch';
import { API } from '../constants';
import type { User } from '../types/dvms.types';
import type { PageParams, PageResponse, BulkImportResult } from './crud';

/** Escape a value for inclusion in a CSV cell (RFC 4180). No formula sanitization — this CSV is sent to the backend, not opened in a spreadsheet. */
function csvEscapeUser(value: string): string {
    if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
        return '"' + value.replace(/"/g, '""') + '"';
    }
    return value;
}

interface BackendSyncFailure {
    email: string;
    error: string;
}

export interface SyncAllResult {
    syncedToOkta: User[];
    syncedToDB: User[];
    failedToOkta: BackendSyncFailure[];
    failedToDB: BackendSyncFailure[];
}

interface BackendSyncAllResponse {
    syncedToOkta: BackendUserResponse[] | null;
    syncedToDB: BackendUserResponse[] | null;
    failedToOkta: BackendSyncFailure[] | null;
    failedToDB: BackendSyncFailure[] | null;
}

interface BackendUserResponse {
    id: string;
    displayId?: string;
    email: string;
    firstName: string;
    lastName: string;
    status: string;
    role: string;
}

function toUser(r: BackendUserResponse): User {
    return {
        id: r.id,
        ...(r.displayId ? { displayId: r.displayId } : {}),
        name: `${r.firstName} ${r.lastName}`.trim(),
        email: r.email,
        role: (r.role || 'project_reader') as User['role'],
        oktaStatus: r.status || 'unknown',
    };
}

export async function listUsers(): Promise<User[]> {
    const limit = 200;
    let page = 0;
    const all: User[] = [];
    let hasMore = true;
    while (hasMore) {
        const resp = await apiFetch<PageResponse<BackendUserResponse>>(`${API.USERS}?page=${page}&limit=${limit}`);
        all.push(...resp.data.map(toUser));
        hasMore = all.length < resp.total && resp.data.length === limit;
        page += 1;
    }
    return all;
}

/** A user's ID and name only — open to every role, unlike {@link listUsers} (DUBVM-151). */
export interface UserName {
    id: string;
    name: string;
}

export async function searchUserNames(query: string, signal?: AbortSignal): Promise<UserName[]> {
    const opts: RequestInit = signal ? { signal } : {};
    const resp = await apiFetch<{ users: UserName[] | null }>(
        `${API.USER_NAMES}?filter=${encodeURIComponent(query)}`,
        opts,
    );
    return resp.users ?? [];
}

export async function createUser(payload: User, createdBy?: string): Promise<User> {
    const parts = payload.name.split(' ');
    const firstName = parts[0] ?? '';
    const lastName = parts.slice(1).join(' ') || firstName;
    const resp = await apiFetch<BackendUserResponse>(API.USERS, {
        method: 'POST',
        body: JSON.stringify({
            firstName,
            lastName,
            email: payload.email,
            role: payload.role,
            ...(createdBy ? { createdBy } : {}),
        }),
    });
    return toUser(resp);
}

/**
 * Update a user.
 * @param isOktaSync - if true, backend retries Okta user creation (resync).
 *                     if false, backend updates user info and syncs to Okta + DB.
 */
export async function updateUser(payload: Partial<User> & { id: string }, isOktaSync = false): Promise<User> {
    const parts = (payload.name ?? '').split(' ');
    const firstName = parts[0] ?? '';
    const lastName = parts.slice(1).join(' ') || firstName;
    const resp = await apiFetch<BackendUserResponse>(`${API.USERS}/${payload.id}`, {
        method: 'PUT',
        body: JSON.stringify({ firstName, lastName, email: payload.email, role: payload.role, isOktaSync }),
    });
    return toUser(resp);
}

export async function deleteUser(id: string): Promise<void> {
    await apiFetch(`${API.USERS}/${id}`, { method: 'DELETE' });
}

export async function listUsersPaged(params: PageParams): Promise<{ data: User[]; total: number }> {
    const result = await apiFetch<PageResponse<BackendUserResponse>>(
        `${API.USERS}?page=${params.page}&limit=${params.limit}`,
    );
    return { data: result.data.map(toUser), total: result.total };
}

export async function countUsers(): Promise<number> {
    const result = await apiFetch<{ count: number }>(`${API.USERS}/count`);
    return result.count;
}

export async function syncAllUsers(): Promise<SyncAllResult> {
    const resp = await apiFetch<BackendSyncAllResponse>(`${API.USERS}/sync`, { method: 'POST' }, 120_000);
    return {
        syncedToOkta: (resp.syncedToOkta ?? []).map(toUser),
        syncedToDB: (resp.syncedToDB ?? []).map(toUser),
        failedToOkta: resp.failedToOkta ?? [],
        failedToDB: resp.failedToDB ?? [],
    };
}

export async function importUsers(payload: User[]): Promise<BulkImportResult> {
    if (payload.length === 0) {
        return { total: 0, created: 0, updated: 0, failed: 0 };
    }

    const headers = ['id', 'name', 'email', 'role'];
    const lines = [headers.join(',')];
    for (const u of payload) {
        lines.push([u.id, u.name, u.email, u.role].map(csvEscapeUser).join(','));
    }
    return apiFetch<BulkImportResult>(
        `${API.USERS}/csv`,
        {
            method: 'POST',
            body: lines.join('\n') + '\n',
            headers: { 'Content-Type': 'text/csv' },
        },
        120_000,
    );
}
